/**
 * alertCountsBus — "an alert just changed hands; whoever is showing a count should re-ask".
 *
 * WHY THIS EXISTS
 * ---------------
 * The title bar's alert meters poll, because alerts also arrive on their own. A poll is the
 * wrong mechanism for the operator's OWN actions, though: acknowledge or resolve an alert and
 * the number beside the clock stayed put for up to a minute, which reads as a broken badge
 * rather than a slow one — the one case where the person is watching for the number to move
 * is the case where they just made it move themselves.
 *
 * A socket subscription in the header would also work, but the header would then need one per
 * app (the node has its own socket, the cloud fans out per site) to learn about something the
 * app already knows locally: the mutation it just performed. So this is a one-line in-process
 * signal, fired where the lifecycle calls succeed. The poll stays as the backstop for changes
 * made by somebody else.
 *
 * Deliberately carries NO payload. A meter's job on hearing this is to re-ask its own
 * endpoint, not to patch a number it half-knows — a badge that maintains its own arithmetic
 * drifts from the database in exactly the situations (bulk actions, another operator, a failed
 * request) where it matters.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Tell every meter to refresh. Safe to call from anywhere, including a `.then()`. */
export function notifyAlertCountsChanged(): void {
  // A copy, so a listener that unsubscribes itself while we are notifying cannot break the walk.
  [...listeners].forEach((fn) => {
    try {
      fn();
    } catch {
      /* one broken listener must not stop the others from refreshing */
    }
  });
}

/** Subscribe. Returns the unsubscribe, for a useEffect cleanup. */
export function onAlertCountsChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
